// Pilotage du moteur de mesure dans un Web Worker.
import type { EngineOptions, Progress, TestResult, WorkerIn, WorkerOut } from '@mesurio/engine';
import { probeServer } from '@mesurio/engine';
import type { PublicServer } from './api';

export interface RunHandle {
  done: Promise<TestResult>;
  abort: () => void;
}

export function runInWorker(server: string, onProgress: (p: Progress) => void, opts: Partial<Omit<EngineOptions, 'signal' | 'server'>> = {}): RunHandle {
  const w = new Worker(new URL('./engine.worker.ts', import.meta.url), { type: 'module' });
  let rejectFn: (e: Error) => void = () => {};
  const done = new Promise<TestResult>((resolve, reject) => {
    rejectFn = reject;
    w.onmessage = (ev: MessageEvent<WorkerOut>) => {
      const m = ev.data;
      if (m.type === 'progress') onProgress(m.progress);
      else if (m.type === 'result') {
        resolve(m.result);
        w.terminate();
      } else {
        reject(Object.assign(new Error(m.message), { invalid: m.invalid }));
        w.terminate();
      }
    };
    w.onerror = (e) => {
      reject(new Error(e.message || 'erreur du moteur de mesure'));
      w.terminate();
    };
  });
  w.postMessage({ type: 'start', options: { ...opts, server } } satisfies WorkerIn);
  return {
    done,
    abort: () => {
      w.postMessage({ type: 'abort' } satisfies WorkerIn);
      setTimeout(() => {
        w.terminate();
        rejectFn(Object.assign(new Error('test annulé'), { invalid: true }));
      }, 1500);
    },
  };
}

/** Sélection : les 5 serveurs les plus proches, puis la plus faible latence médiane. */
export async function pickServer(servers: PublicServer[]): Promise<PublicServer | null> {
  const candidates = servers.slice(0, 5);
  let best: PublicServer | null = null;
  let bestRTT = Infinity;
  for (const s of candidates) {
    try {
      const rtt = await probeServer(s.url, AbortSignal.timeout(4000));
      if (rtt < bestRTT) {
        bestRTT = rtt;
        best = s;
      }
    } catch {
      /* serveur injoignable : ignoré */
    }
  }
  return best;
}
